import type { Copy } from '@/i18n/en'
import { format } from '@/i18n/format'
import type { ElapsedBand, TimeOfDay } from './elapsed'
import { elapsedBand, timeOfDay } from './elapsed'

/**
 * The first thing Namu says, chosen from what it already knows.
 *
 * Two phrasings said in rotation is still a recording, and a person hears the
 * seam in it by the third day. What makes an assistant feel present rather
 * than played back is that the greeting knows something: how long you have
 * been gone, what time it is where you are, and, if you allowed it to keep
 * that, what you asked about last time.
 *
 * Every input is a value passed in. There is no clock read here, no store
 * reached into and no request made, which is what lets a test cover the month
 * band without waiting a month and what lets the first word be spoken the
 * instant the tap lands.
 */

/** Every case this produces a line for, including the one with no history. */
export type GreetingBand = 'firstEver' | ElapsedBand

/**
 * How a phrasing is chosen from a set. The session's `VariantRotator` fits
 * this signature; the default takes the first, so tests that are about which
 * band was chosen do not have to reason about which wording came up.
 */
export type Rotate = (key: string, variants: readonly string[]) => string

export interface GreetingInput {
  copy: Copy
  /** Milliseconds since the epoch, injected so every band is reachable. */
  now: number
  /** Null when Namu has no name for this person, which is not the same as no history. */
  name: string | null
  /** Null on a first-ever visit. */
  lastSpokeAt: number | null
  topic: string | null
  /** Whether the person agreed to Namu keeping what is said between conversations. */
  consented: boolean
  rotate?: Rotate
}

export interface Greeting {
  /** The whole utterance, ready to speak. */
  text: string
  band: GreetingBand
  /** Set only for a return on a later day, which is the case it is chosen for. */
  timeOfDay: TimeOfDay | null
  /** Whether the line about last time was included. */
  continuity: boolean
  /**
   * Whether the conversation still needs an explicit "I am listening" after
   * this. See `needsInvitation`.
   */
  invite: boolean
}

/** The eight lines a returning person can be greeted with. */
type LineKey = 'moments' | 'sameDay' | 'week' | 'month' | TimeOfDay

const firstVariant: Rotate = (_key, variants) => variants[0] ?? ''

/**
 * Whether the greeting has already invited the person to speak.
 *
 * "Yes, Amina?" is a question and it hands the turn over on its own; following
 * it with "I am listening" makes Namu sound like it is talking to itself.
 * "Good morning, Amina." is not a question, and a person who hears it followed
 * by silence does not know the microphone is open, which on a screen they may
 * not be looking at is the difference between using the product and putting
 * the phone down. The first-ever line ends by saying what to do, so it needs
 * nothing after it either.
 */
function needsInvitation(band: GreetingBand): boolean {
  return band !== 'firstEver' && band !== 'moments'
}

/**
 * Whether to add what the last conversation was about.
 *
 * Not on the two short bands, and that is a judgement rather than a technical
 * limit. Somebody who was here twenty minutes ago, or earlier this same day,
 * has not forgotten what they asked; reciting it back to them is not warmth,
 * it is a machine proving it took notes. From the next day onward it is the
 * line that makes the difference between a product that greets you and one
 * that knows you.
 */
function wantsContinuity(band: GreetingBand): boolean {
  return band === 'newDay' || band === 'week' || band === 'month'
}

/**
 * Which of the eight lines a band asks for, and the hour behind it.
 *
 * Only a return on a later day is greeted by the time of day. "Good morning"
 * said to somebody who has been talking since breakfast is a machine
 * announcing that it has consulted a clock. What the hour is for is the return
 * after a night, which is the case the whole distinction was drawn for, so the
 * other four bands do not read it at all.
 */
function lineFor(band: ElapsedBand, now: number): { line: LineKey; hour: TimeOfDay | null } {
  if (band !== 'newDay') return { line: band, hour: null }
  const hour = timeOfDay(now)
  return { line: hour, hour }
}

export function selectGreeting(input: GreetingInput): Greeting {
  const rotate = input.rotate ?? firstVariant
  const greeting = input.copy.speech.greeting

  /*
   * A first visit is its own case, not the returning greeting with the name
   * left out. There is no name on either side yet, so what this line does is
   * speak in both languages: the locale at this point is a guess made from the
   * browser's settings, and the reply is what actually decides which language
   * the conversation happens in. Every other product in this space opens with
   * a language menu, which is a form, and a form is the one thing this product
   * does not do.
   *
   * It does not say who is speaking. The persona introduces itself when it
   * answers, from the backend, and a name said here would be the device
   * claiming to know which one is on the other end of the socket.
   *
   * `band` is returned rather than only the text so that a caller can hand
   * this case off. The onboarding flow's own opening covers the same moment,
   * and when it is wired in it takes the first visit and this line becomes
   * what somebody who abandoned or skipped onboarding hears instead.
   */
  if (input.lastSpokeAt === null) {
    return {
      text: rotate('greeting.first', greeting.first),
      band: 'firstEver',
      timeOfDay: null,
      continuity: false,
      invite: false,
    }
  }

  const band = elapsedBand(input.lastSpokeAt, input.now)
  const { line, hour } = lineFor(band, input.now)

  /*
   * A person with no name is not a person with no history. Somebody who
   * declined to have their answers kept still gets a greeting that knows what
   * day it is, because the profile store holds nothing for them and there is
   * nothing in that fact to hold against them. The two sets are written out
   * separately rather than assembled from a stem and a name, because "Amina."
   * leads the month line in English and trails the day greeting in Hausa, and
   * a sentence built from parts gets one of those wrong in one language.
   */
  const named = input.name !== null
  const variants = named ? greeting.named[line] : greeting.anonymous[line]
  const opener = format(rotate(`greeting.${named ? 'named' : 'anonymous'}.${line}`, variants), {
    name: input.name ?? '',
  })

  /*
   * The only line here that consent covers, so the only one that checks. The
   * check is on the value read from the device at greeting time rather than on
   * a flag captured when the topic was written: consent can be withdrawn
   * between two conversations, and a topic stored under the old answer is
   * still on the disk when it is.
   */
  const continuity = input.consented && input.topic !== null && wantsContinuity(band)
  const text = continuity
    ? `${opener} ${format(rotate('greeting.continuity', greeting.continuity), { topic: input.topic ?? '' })}`
    : opener

  return { text, band, timeOfDay: hour, continuity, invite: needsInvitation(band) }
}
